import { LitElement, html, css, unsafeHTML } from 'https://cdn.jsdelivr.net/gh/lit/dist@3/all/lit-all.min.js'

/**
 * Skio Universal Plan Picker
 * @docs https://integrate.skio.com/
 *
 * Uses LitJS (<5kb) https://lit.dev/ for reactive web components
 */

export class SkioPlanPicker extends LitElement {
  static properties = {
    key: { type: String },

    product: { type: Object },
    productHandle: { type: String },
    selectedVariant: { type: Object },

    options: { type: Object },

    quantity: { type: Number },
    rules: { type: Array },
    rule: { type: Object },
    cart: { type: Object },

    availableSellingPlanGroups: { state: true },
    selectedSellingPlanGroup: { type: Object },
    selectedSellingPlan: { type: Object },

    disableUrl: { type: Boolean },

    refreshUi: { type: Boolean, attribute: 'refresh-ui' },

    debug: { type: Boolean }
  }

  static styles = css`
    :host {
      display: block;
      width: 100%;
      position: relative;
      padding-top: 12px;
    }

    .skio-plan-picker {
      border: none;
      padding: 0;
      min-inline-size: 0
    }

    /* Save Badge */
    .save-badge {
      background-color: #174332;
      color: white;
      font-size: 13px;
      font-weight: 900;
      font-family: 'Avenir-Black', sans-serif;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      padding: 10px 20px;
      border-radius: 20px;
      text-align: center;
      position: absolute;
      top: -12px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 1;
    }

    /* Group Container */
    .group-container {
      padding: 14px 10px;
      margin-bottom: 10px;
      cursor: pointer;
      transition: all 0.2s ease;
      border-radius: 10px;
    }

    .group-container:not(.group-container--selected) {
      border: 1px solid #719C90;
    }

    .group-container--selected {
      background: #E7F1EE;    
      border: 1px solid #174332;
    }

    .group-container--selected.group-container--autoship {
      padding-top: 34px;
    }

    .group-container:not(.group-container--autoship) .group-title {
      text-transform: none !important;
    }

    .group-container .group-topline {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0;
    }
    
    .group-container .group-title {
      font-size: 14px;
      font-weight: 900;
      color: #174332;
      font-family: 'Avenir-Black', sans-serif;
      letter-spacing: 0.4px;
    }

    @media (min-width: 768px) {
      .group-container {
        padding: 20px;
        margin-bottom: 20px;
      }

      .group-container--selected.group-container--autoship {
        padding-bottom: 20px;
      }

      .group-container .group-title {
        font-size: 18px;
      }

      .save-badge {
        font-size: 16px;
      }
    }

    /* Subscription content inherits selected state */
    .product_frequency-wrap--message {
      background: inherit;
      border-radius: inherit;
    }

    /* Hide actual radio button */
    .group-input {
      display: none;
    }

    /* Custom radio button */
    .group-label {
      position: relative;
      padding-left: 25px;
      cursor: pointer;
      display: block;
    }

    .group-label:before {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 16px;
      height: 16px;
      border: 2px solid #174332;
      border-radius: 50%;
    }

    @media (min-width: 400px) {
      .group-label {
        padding-left: 30px; 
      }
    }

    /* Apply the filled circle for both checked state and selected group */
    .group-input:checked + .group-label:after,
    .group-container--selected .group-label:after {
      content: '';
      position: absolute;
      left: 4px;
      top: 50%;
      transform: translateY(-50%);
      width: 12px;
      height: 12px;
      border-radius: 50%;
      background: #174332;
    }

    /* Savings Tiers */
    .savings-tiers {
      display: flex;
      justify-content: space-between;
      gap: 8px;
      margin: 20px 0;
      padding: 0 8px;
    }

    .tier {
      flex: 1;
      text-align: center;
      line-height: 1.4;
      border-right: 2px solid #174332;
      padding-right: 8px;
    }

    .tier:last-child {
      border-right: none;
      padding-right: 0;
    }

    .tier > div:first-child {
      font-weight: 700;
      text-transform: uppercase;
      font-size: 12px;
      color: #2B4636;
    }

    .tier > div:nth-child(2) {
      color: #00A362;
      font-family: 'Avenir-Black', sans-serif;
      font-weight: 900;
      font-size: 14px;
      margin-top: 2px;
    }

    .tier > div:last-child {
      color: #2B4636;
      font-size: 10px;
      letter-spacing: 0.1px;
      margin-top: 2px;
    }

    @media (min-width: 400px) {
      .tier > div:first-child {
        font-size: 14px;
      }

      .tier > div:nth-child(2) {
        font-size: 14px;
      }

      .tier > div:last-child {
        font-size: 12px;
      }
    }

    @media (min-width: 768px) {
      .tier > div:nth-child(2) {
        font-size: 16px;
      }
    }

    /* Frequency Section */
    .frequency-selector-wrapper {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0;
      margin-top: 20px;
    }

    .frequency-label {
      display: block;
      font-size: 14px;
      font-weight: 700;
      color: #2B4636;
      margin-right: 12px;
    }

    .selling-plan-dropdown {
      width: 100%;
      height: 36px;
      border: 2px solid #174332;
      border-radius: 5px;
      background-color: transparent;
      font-size: 14px;
      color: #174332;
      font-size: 12px;
      font-weight: 900;
      padding-left: 10px;
    }

    @media (min-width: 768px) {
      .frequency-selector-wrapper {
        margin-top: 30px;
      }
    }

    /* Price Styles */
    .skio-price {
      font-weight: 900;
      font-size: 14px;
      color: #174332;
      font-family: 'Avenir-Black', sans-serif;
    }

    .skio-price s {
      font-weight: 500;
      font-size: 14px;
      font-family: 'Avenir-Book', sans-serif;
    }

    .skio-price--discounted {
      font-family: 'Avenir-Black', sans-serif;
      font-weight: 900;
      font-size: 16px;
    }

    @media (min-width: 400px) {
      .skio-price {
        font-size: 16px;
      }

      :host([product-page]) .skio-price {
        flex-wrap: wrap;
      }

      :host([product-card]) .skio-price {
        letter-spacing: normal;
        font-size: 14px;
        font-family: 'Avenir-Black';
        align-self: flex-start;
        color: #174332;
        margin-left: unset;
        margin-bottom: 0.5rem;
      }

      :host([product-card]) .skio-price s {
        font-size: 13px;
        font-family: 'Avenir';
      }

      .skio-price s {
        font-size: 14px;
      }
    }

    @media (min-width: 768px) {
      .skio-price {
        font-size: 20px;
      }

      .skio-price s {
        font-size: 18px;
      }

      .skio-price--discounted {
        font-size: 20px;
      }
    }

    /* One-time Purchase */
    .group-container:last-child .group-title {
      text-transform: uppercase;
      font-weight: 700;
      letter-spacing: 0.05em;
      font-family: 'Avenir-Book', sans-serif;
    }

    /* PDP Refresh variant — active only when the host element carries the
       refresh-ui attribute, so control-template PDPs are pixel-identical */
    :host([refresh-ui]) .group-container--autoship .group-title {
      text-transform: none;
      letter-spacing: 0;
    }

    /* Selects don't inherit the page font — set the theme face explicitly
       (weight normal: the Avenir faces are registered at 400) */
    :host([refresh-ui]) .selling-plan-dropdown {
      font-family: 'Avenir-Medium', Avenir, -apple-system, BlinkMacSystemFont, sans-serif;
      font-weight: normal;
    }

    @media (max-width: 767px) {
      :host([refresh-ui]) .group-container {
        padding: 12px 10px;
        margin-bottom: 8px;
      }

      :host([refresh-ui]) .savings-tiers {
        margin: 12px 0 4px;
      }

      :host([refresh-ui]) .frequency-selector-wrapper {
        margin-top: 12px;
      }
    }

    @media (min-width: 768px) {
      /* Desktop tightening so the CTA clears the fold */
      :host([refresh-ui]) .group-container {
        padding: 14px 16px;
        margin-bottom: 12px;
      }

      :host([refresh-ui]) .group-container--selected.group-container--autoship {
        padding-top: 28px;
        padding-bottom: 14px;
      }

      :host([refresh-ui]) .savings-tiers {
        margin: 12px 0;
      }

      :host([refresh-ui]) .frequency-selector-wrapper {
        margin-top: 12px;
      }
    }
  `

  constructor() {
    super()

    this.currency = window.Shopify.currency.active || 'USD'
    this.language = window.Shopify.locale || 'en-US'
    this.moneyFormatter = new Intl.NumberFormat(this.language, {
      style: 'currency',
      currency: this.currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })

    // Start - Debug Variables
    this.debug = window?.Shopify?.designMode
    this.variantChanged = false
    // End - Debug Variables

    this.rules = []
    this.rule = null
    this.quantity = 1
  }

  async connectedCallback() {
    super.connectedCallback()
    this.log('Mounted')

    if (this.productHandle) {
      this.loading = true
      await this.fetchProduct(this.productHandle)
    }

    this.bindFormEvents()
    this.bindCartEvents()
    this.bindQuantityEvents()

    this.rules = await this.fetchRules()
    await this.getRule()

    // Set the default selection based on the theme setting (options.start_onetime).
    if (this.options?.start_onetime) {
      // Default to one-time purchase: no group, no selling plan.
      this.selectSellingPlanGroup(null);
    } else if (this.availableSellingPlanGroups && this.availableSellingPlanGroups.length > 0) {
      // Default to subscription (unchanged): select the first subscription plan.
      const defaultPlanGroup = this.availableSellingPlanGroups[0];
      if (defaultPlanGroup) {
        this.selectSellingPlanGroup(defaultPlanGroup);
        // Always select the first plan on initial load
        if (defaultPlanGroup.selling_plans.length > 0) {
          this.selectSellingPlan(defaultPlanGroup.selling_plans[0].id);
        }
      }
    }
  }

  // SECTION: Element Templates
  radioTemplate() {
    return html`
      <div class="skio-radio__container">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1"></circle>
          <circle class="skio-radio" cx="12" cy="12" r="7" fill="currentColor"></circle>
        </svg>
      </div>
    `
  }

  radioTemplateProductCard(group) {
    return html`
      <div class="skio-radio__container">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle
            cx="12"
            cy="12"
            r="10"
            fill="${this.selectedSellingPlanGroup == group ? 'currentColor' : 'none'}"
            stroke="currentColor"
            stroke-width="1"
          ></circle>
          <circle class="skio-radio" cx="12" cy="12" r="4" fill="white"></circle>
        </svg>
      </div>
    `
  }

  sellingPlanDropdown(group) {
    return html`
      <select
        class="selling-plan-dropdown ${this.getAvailableSellingPlans(group).length == 1 ? 'selling-plan-dropdown--one' : ''}"
        @change=${(e) => {
          this.selectSellingPlan(e.target.value)
        }}
      >
        ${group
          ? this.getAvailableSellingPlans(group).map(
              (selling_plan) =>
                html` <option ?selected=${group.selected_selling_plan.id === selling_plan.id} value=${selling_plan.id}>${selling_plan.name}</option> `
            )
          : ''}
      </select>
    `
  }

  sellingPlanButtons(group) {
    return html`
      <fieldset class="selling-plan-buttons">
        <legend class="sr-only">Select subscription interval</legend>
        ${group
          ? this.getAvailableSellingPlans(group).map(
              (selling_plan) => html`
                <input
                  type="radio"
                  name="selling_plan_button"
                  value="${selling_plan}"
                  id="selling_plan_button-${selling_plan.id}"
                  @change="${(e) => this.selectSellingPlan(selling_plan.id)}"
                  ?checked=${group.selected_selling_plan === selling_plan}
                />

                <label for="selling_plan_button-${selling_plan.id}"> ${selling_plan.name} </label>
              `
            )
          : ''}
      </fieldset>
    `
  }

  invalidIcon() {
    return html`
      <svg
        width="24"
        height="24"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke-width="1.5"
        stroke="#D22B2B"
        class="w-6 h-6"
      >
        <path stroke-linecap="round" stroke-linejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    `
  }

  validIcon() {
    return html`
      <svg
        width="24"
        height="24"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke-width="1.5"
        stroke="#0BDA51"
        class="w-6 h-6"
      >
        <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    `
  }

  setupMode() {
    return html`
      <div class="setupMode">
        Skio Plan Picker
        <small>This will not appear on the live site.</small>

        <small>Key: ${this.key}</small>

        <div class="detection">${this.product ? this.validIcon() : this.invalidIcon()} Product</div>

        <div class="detection">${this.selectedVariant ? this.validIcon() : this.invalidIcon()} Selected Variant</div>

        <div class="detection">${this.form ? this.validIcon() : this.invalidIcon()} Form</div>

        <div class="detection">${this.variantInput ? this.validIcon() : this.invalidIcon()} Variant Input</div>

        <div class="detection">
          ${this.variantChanged ? this.validIcon() : this.invalidIcon()} Variant Change Detection
          <small>Please attempt to change the selected variant.</small>
        </div>

        <small
          >If any of these don't pass please troubleshoot using our
          <a target="_blank" rel="noreferrer" href="https://integrate.skio.com/troubleshooting">troubleshooting guide</a>.</small
        >
      </div>
    `
  }

  groupContent(group) {
    return html`
      <div class="group-content ${this.options?.show_radio_selector ? 'margin-left' : ''}">
        ${this.options?.selector_type == 'dropdown' ? this.sellingPlanDropdown(group) : ''}

        <!-- TODO: Make the label have the border instead of the group -->
        ${this.options?.selector_type == 'button' ? this.sellingPlanButtons(group) : ''} ${this.additionalContentText()}
      </div>
    `
  }
  // !SECTION: Element Templates

  render() {
    if (!this.viable() || this.options?.layout === 'hidden') {
      return this.debug ? this.setupMode() : ''
    }

    if (this.selectedVariant.selling_plan_allocations.length === 0 && !this.options?.show_without_subscription) {
      return this.debug ? this.setupMode() : ''
    }

    // Calculate savings for different quantities
    const calculateSavings = (quantity) => {
      const oneTimePrice = this.selectedVariant.price
      let discount = 0

      if (quantity === 1) discount = 0.1
      else if (quantity === 2) discount = 0.15
      else if (quantity >= 3) discount = 0.25

      // Calculate savings per item instead of total savings
      const savingsPerItem = oneTimePrice * discount
      return {
        percentage: discount * 100,
        amount: this.money(savingsPerItem)
      }
    }

    return html`
      <div class="skio-plan-picker-wrapper">
        <!-- Save up to 25% badge - only shows if subscription is available -->
        ${this.availableSellingPlanGroups?.length ? html` <div class="save-badge">SAVE UP TO 25%</div> ` : ''}

        <fieldset class="skio-plan-picker" role="radiogroup" aria-labelledby="skio-plan-picker-legend">
          <!-- Autoship Option -->
          ${this.availableSellingPlanGroups?.length
            ? this.availableSellingPlanGroups.map(
                (group, index) => html`
                  <div 
                    class="group-container ${this.selectedSellingPlanGroup == group ? 'group-container--selected' : ''} ${group ? 'group-container--autoship' : 'group-container--onetime'}"
                    @click=${(e) => {
                      e.preventDefault();
                      this.selectSellingPlanGroup(group);
                    }}
                  >
                    <input
                      id="group-${index}-${this.key}"
                      class="group-input"
                      name="skio-group-${this.key}"
                      type="radio"
                      value="${group.id}"
                      ?checked=${this.selectedSellingPlanGroup == group}
                      @change=${() => this.selectSellingPlanGroup(group)}
                    />
                    <label class="group-label" for="group-${index}-${this.key}">
                      <div class="group-topline">
                        <div class="group-title">${this.refreshUi ? 'Autoship & Save' : 'AUTOSHIP & SAVE!'}</div>
                        <div class="skio-price skio-price--autoship">
                          <s class="skio-price--strike">${this.money(this.selectedVariant.price)}</s>
                          <span class="skio-price--discounted">
                            ${group.selling_plans.length > 0 
                              ? this.price(group.selling_plans[0]) 
                              : this.money(this.selectedVariant.price)}
                          </span>
                        </div>
                      </div>
                    </label>

                    <!-- Subscription Content - Moved inside group container -->
                    ${this.selectedSellingPlanGroup == group
                      ? html`
                          <div class="product_frequency-wrap--message active">
                            <div class="savings-tiers">
                              <div class="tier">
                                <div>1 ITEM</div>
                                <div>10% OFF</div>
                                <div>Save ${calculateSavings(1).amount}/item</div>
                              </div>
                              <div class="tier">
                                <div>2 ITEMS</div>
                                <div>15% OFF</div>
                                <div>Save ${calculateSavings(2).amount}/item</div>
                              </div>
                              <div class="tier">
                                <div>3+ ITEMS</div>
                                <div>25% OFF</div>
                                <div>Save ${calculateSavings(3).amount}/item</div>
                              </div>
                            </div>
                            <div
                              class="frequency-selector-wrapper"
                              @mousedown=${(e) => e.stopPropagation()}
                              @click=${(e) => e.stopPropagation()}
                            >
                              <label for="frequency-select-${this.key}" class="frequency-label">Frequency</label>
                              <select
                                id="frequency-select-${this.key}"
                                class="selling-plan-dropdown"
                                @change=${(e) => {
                                  e.stopPropagation();
                                  const newPlanId = e.target.value;
                                  this.selectSellingPlan(newPlanId);
                                }}
                              >
                                ${this.selectedSellingPlanGroup
                                  ? this.getAvailableSellingPlans(this.selectedSellingPlanGroup).map(
                                      (selling_plan) => html`
                                        <option
                                          value=${selling_plan.id}
                                          ?selected=${this.selectedSellingPlan?.id === selling_plan.id}
                                        >
                                          ${selling_plan.name}
                                        </option>
                                      `
                                    )
                                  : ''}
                              </select>
                            </div>
                          </div>
                        `
                      : ''}
                  </div>
                `
              )
            : ''}

          <!-- One-time Purchase Option -->
          ${!this.product.requires_selling_plan
            ? html`
                <div
                  class="group-container ${this.selectedSellingPlanGroup == null ? 'group-container--selected' : ''}"
                  @click=${(e) => {
                    e.preventDefault();
                    this.selectSellingPlanGroup(null);
                  }}
                >
                  <input
                    id="one-time-${this.key}"
                    class="group-input"
                    name="skio-group-${this.key}"
                    type="radio"
                    value="One time purchase"
                    ?checked=${!this.selectedSellingPlanGroup}
                    @change=${() => this.selectSellingPlanGroup(null)}
                  />

                  <label class="group-label" for="one-time-${this.key}">
                    <div class="group-topline">
                      <div class="group-title">One-time purchase</div>
                        <div class="skio-price__wrapper">
                        ${this.options.show_compare_price && this.selectedVariant.compare_at_price > 0 ? html`<div class="skio-compare-price"><del>${this.money(this.selectedVariant.compare_at_price)}</del></div>` : ""}
                        <div class="skio-price skio-price--onetime">${this.money(this.selectedVariant.price)}</div>
                      </div>
                    </div>
                  </label>
                </div>
              `
            : ''}
        </fieldset>
      </div>
    `
  }

  async fetchRules() {
    if (sessionStorage.getItem('skio-discount-rules')) {
      return JSON.parse(sessionStorage.getItem('skio-discount-rules'))
    } else {
      const rules = await fetch(`https://api.skio.com/storefront-http/get-rules-by-domain-or-hostname?domain=${window?.Shopify?.shop}`)
        .then((response) => response.json())
        .then((response) => {
          return response.rules.filter((rule) => rule.type !== 'surpriseDelight').sort((a, b) => a.minQuantityToDiscount - b.minQuantityToDiscount)
        })

      sessionStorage.setItem('skio-discount-rules', JSON.stringify(rules))
      return rules
    }
  }

  eligibleItemCount(rule) {
    if (!this.cart?.items) return this.quantity

    let quantity = this.quantity
    let savedby = this.cart?.items.find((item) => item.vendor === 'SavedBy')
    if (savedby) {
      quantity -= savedby.quantity
    }

    let cartItems = Number(
      this.cart?.items
        .filter((item) => item.vendor !== 'SavedBy')
        .reduce(
          (aggregate, item) =>
            rule?.productVariantIds.some((gid) => gid.includes(item.id) && item.selling_plan_allocation) ? aggregate + item.quantity : aggregate,
          0
        )
    )
    return cartItems + quantity
  }

  getRule() {
    let largestMinQuantity = {
      value: -1,
      index: -1
    }
    this.rules.forEach((rule, index) => {
      if (rule.code && largestMinQuantity.value < rule.minQuantityToDiscount) {
        if (rule.minQuantityToDiscount <= this.eligibleItemCount(rule)) {
          largestMinQuantity.value = rule.minQuantityToDiscount
          largestMinQuantity.index = index
        }
      }
    })

    this.rule = largestMinQuantity.index > -1 ? this.rules[largestMinQuantity.index] : null
  }

  bindCartEvents() {
    document.addEventListener('CartUpdated', (event) => {
      this.cart = event.detail
    })
  }

  bindQuantityEvents() {
    let quantityInput = this.form.querySelector('.quantity-selector')

    if (quantityInput) {
      quantityInput.addEventListener('change', () => {
        this.quantity = Number(quantityInput.value)
      })
    }
  }

  bindFormEvents() {
    this.form = document.querySelector(`#${this.options?.form_id}`) || this.closest('form[action*="/cart/add"]')

    if (!this.form) {
      return
    }

    this.variantInput = this.form.querySelector('[name="id"]')

    if (this.variantInput) {
      this.variantInput.addEventListener('change', (e) => {
        this.selectedVariant = this.product.variants.find((variant) => variant.id == e.target.value)
        this.variantChanged = true
      })
    }
  }

  updated(changed) {
    if (!this.viable()) return;

    if (changed.has('product') && this.product) {
      this.key = this.key ? this.key : this.product.id;
    }

    if (changed.has('selectedVariant') && this.selectedVariant) {
      const filteredSellingPlanGroups = this.product.selling_plan_groups.filter(
        (selling_plan_group) =>
          selling_plan_group.app_id === 'SKIO' &&
          !selling_plan_group.name.toLowerCase().includes('hidden-') &&
          !selling_plan_group.name.toLowerCase().includes('dynamic box subscription')
      );

      this.availableSellingPlanGroups = filteredSellingPlanGroups.filter((selling_plan_group) =>
        selling_plan_group.selling_plans.some((selling_plan) =>
          this.selectedVariant.selling_plan_allocations.some((selling_plan_allocation) => selling_plan_allocation.selling_plan_id === selling_plan.id)
        )
      );

      // Only set a default subscription plan when the merchant default is subscription
      // (one-time default leaves the selection null even across variant changes).
      if (!this.options?.start_onetime && this.availableSellingPlanGroups?.length && !this.selectedSellingPlan) {
        const defaultPlanGroup = this.availableSellingPlanGroups[0];
        this.selectSellingPlanGroup(defaultPlanGroup);
        // Don't automatically select the first plan if we already have a selection
        if (!this.selectedSellingPlan) {
          this.selectSellingPlan(defaultPlanGroup.selling_plans[0].id);
        }
      }
    }

    if (changed.has('quantity')) {
      this.getRule();
    }

    if (changed.has('cart')) {
      this.getRule();
    }

    this.updateExternalElements();

    // PDP refresh: notify the section so the CTA label and sticky bar can
    // mirror the selected plan ("Add to Cart — Autoship $33.29")
    if (this.refreshUi && this.selectedVariant && (changed.has('selectedSellingPlan') || changed.has('selectedSellingPlanGroup') || changed.has('selectedVariant'))) {
      const isSubscription = !!this.selectedSellingPlanGroup
      const priceText = isSubscription && this.selectedSellingPlan
        ? this.price(this.selectedSellingPlan)
        : this.money(this.selectedVariant.price)
      const planName = (isSubscription && this.selectedSellingPlan?.name) || ''
      this.dispatchEvent(new CustomEvent('pdp:plan-change', { detail: { isSubscription, priceText, planName }, bubbles: true }))
    }
  }

  updateExternalElements() {
    // Initialize query selectors with more specific classes
    const autoshipPriceElements = document.querySelectorAll('.skio-price--discounted');
    const autoshipStrikePriceElements = document.querySelectorAll('.skio-price--strike');
    const onetimePriceElements = document.querySelectorAll('.skio-price--onetime');
    const subscriptionContentElements = document.querySelectorAll('.product_frequency-wrap--message');

    // Update autoship price (subscription price)
    if (autoshipPriceElements?.length) {
      autoshipPriceElements.forEach((el) => {
        el.textContent = this.selectedSellingPlan ? 
          this.price(this.selectedSellingPlan) : 
          this.money(this.selectedVariant.price);
      });
    }

    // Update autoship strikethrough price
    if (autoshipStrikePriceElements?.length) {
      autoshipStrikePriceElements.forEach((el) => {
        el.textContent = this.money(this.selectedVariant.price);
      });
    }

    // Update one-time price (regular price)
    if (onetimePriceElements?.length) {
      onetimePriceElements.forEach((el) => {
        el.textContent = this.money(this.selectedVariant.price);
      });
    }

    // Update subscription content visibility
    if (subscriptionContentElements?.length) {
      subscriptionContentElements.forEach((el) => {
        el.style.display = this.selectedSellingPlan ? 'block' : 'none';
      });
    }
  }

  // SECTION: Utility Functions
  log(...args) {
    args.unshift(`%c[skio plan picker][${this.key}]`, 'color: #8770f2;')
  }

  error(...args) {
    args.unshift(`%c [skio plan picker][${this.key}]`, 'color: #ff0000')
    console.error.apply(console, args)
  }

  appendOrdinalSuffix(num) {
    const j = num % 10,
      k = num % 100
    if (j == 1 && k != 11) {
      return num + 'st'
    }
    if (j == 2 && k != 12) {
      return num + 'nd'
    }
    if (j == 3 && k != 13) {
      return num + 'rd'
    }
    return num + 'th'
  }

  money(price) {
    return this.moneyFormatter.format(price / 100.0)
  }

  viable() {
    const errors = []

    const sellingPlanInput = this.querySelector('input[name=selling_plan]')
    if (!sellingPlanInput) {
      errors.push('Missing selling plan input')
    }

    if (!this.product) {
      errors.push('No product found. Please pass a product or productHandle to the skio-plan-picker component.')
    }
    if (!this.selectedVariant) {
      errors.push('No variant found. Please pass a product or productHandle to the skio-plan-picker component.')
    }
    if (!this.form) {
      errors.push(
        'No form found. Please add a form_id to the plan picker through the Shopify customizer or ensure that the element is in a form that has an input with name="id".'
      )
    }
    if (!this.variantInput) {
      errors.push(
        'No variant input found. Please add a form_id to the plan picker through the Shopify customizer or ensure that the element is in a form that has an input with name="id"'
      )
    }

    if (errors.length == 0) return true

    this.error('Errors', errors)
    return false
  }

  getAvailableSellingPlans(group) {
    let availableSellingPlans = group.selling_plans.filter((selling_plan) =>
      this.selectedVariant.selling_plan_allocations.some((selling_plan_allocation) => selling_plan_allocation.selling_plan_id === selling_plan.id)
    )

    return availableSellingPlans
  }

  // !SECTION: Utility Functions

  // Update selected selling plan group; called on click of group-container element
  selectSellingPlanGroup(group) {
    // Force single selection by explicitly managing radio states
    const allRadios = this.querySelectorAll('input[type="radio"]');
    allRadios.forEach(radio => {
      if (group === null) {
        radio.checked = radio.value === "One time purchase";
      } else {
        radio.checked = radio.value === group.id;
      }
    });

    // Update the selected group
    this.selectedSellingPlanGroup = group;

    if (group && group.selling_plans.length > 0) {
      // If there's a currently selected selling plan in the dropdown, use that
      const dropdown = this.shadowRoot.querySelector('.selling-plan-dropdown');
      const currentlySelectedPlanId = dropdown?.value;
      
      if (currentlySelectedPlanId && group.selling_plans.some(plan => plan.id === currentlySelectedPlanId)) {
        const sellingPlan = group.selling_plans.find(plan => plan.id === currentlySelectedPlanId);
        this.selectedSellingPlanGroup.selected_selling_plan = sellingPlan;
        this.selectedSellingPlan = sellingPlan;
      } else {
        const planToSelect = group.selling_plans[0];
        this.selectedSellingPlanGroup.selected_selling_plan = planToSelect;
        this.selectedSellingPlan = planToSelect;
      }
    } else {
      this.selectedSellingPlan = null;
    }

    this.updateForm();
    this.updateExternalElements();
  }

  // Update selected selling plan; called on change of skio-frequency select element
  selectSellingPlan(sellingPlanId) {
    if (!this.selectedSellingPlanGroup) return;

    // The frequency <select> hands us a string; plan ids in the product JSON
    // are numbers. Compare as strings so the lookup works from both callers.
    const sellingPlan = this.selectedSellingPlanGroup.selling_plans.find(
      (plan) => String(plan.id) === String(sellingPlanId)
    );
    
    if (sellingPlan) {
      // Update the selected plan directly
      this.selectedSellingPlan = sellingPlan;
      
      // Update the group's selected plan
      if (this.selectedSellingPlanGroup) {
        this.selectedSellingPlanGroup.selected_selling_plan = sellingPlan;
      }
      
      // Update form and UI
      this.updateForm();
      this.updateExternalElements();

      // Dispatch events
      this.dispatchEvent(
        new CustomEvent('skio::update-selling-plan', {
          bubbles: true,
          composed: true,
          detail: {
            variant: this.selectedVariant,
            sellingPlan: this.selectedSellingPlan,
            key: this.key
          }
        })
      );

      this.dispatchEvent(
        new CustomEvent('change', {
          detail: { value: this.selectedSellingPlan.id }
        })
      );

      // Update price
      if (this.selectedVariant) {
        this.dispatchEvent(
          new CustomEvent('skio::update-price', {
            bubbles: true,
            composed: true,
            detail: { 
              price: this.price(this.selectedSellingPlan)
            }
          })
        );
      }

      // Force a re-render
      this.requestUpdate();
    }
  }

  additionalContentText() {
    if (!this.options?.additional_subscription_content || this.options?.additional_subscription_content == '') return

    const formattedContent = this.options?.additional_subscription_content
      .replaceAll(
        '[discount]',
        this.options?.discount_format === 'absolute'
          ? this.money(this.discount(this.selectedSellingPlan).absolute)
          : this.discount(this.selectedSellingPlan).percent + '%'
      )
      .replaceAll('[future_price_adjustments]', this.postCheckoutDiscountsText(this.selectedSellingPlan) || '')
      .split(' / ') // Split the content by slashes
      .map((item) => `<span class="discount-item">${item}</span>`) // Wrap each item in a span
      .join('')

    return unsafeHTML(formattedContent)
  }

  // SECTION: Discount Functions
  // Calculates discount based on selling_plan.price_adjustments, returns { percent, amount } of selling plan discount
  discountText(selling_plan) {
    const discount = this.discount(selling_plan)

    return unsafeHTML(
      this.options?.discount_text
        .replaceAll('[discount]', this.options?.discount_format === 'absolute' ? this.money(discount.absolute) : discount.percent + '%')
        .replaceAll('[future_price_adjustments]', this.postCheckoutDiscountsText(selling_plan) || '')
    )
  }

  discount(selling_plan) {
    if (!selling_plan) return { percent: 0, amount: 0 }
    return this.getDiscountFromPriceAdjustment(selling_plan, selling_plan.price_adjustments[0])
  }

  postCheckoutPriceAdjustments(selling_plan) {
    const { price_adjustments } = selling_plan
    const postCheckoutPriceAdjustments = price_adjustments.filter(({ position }) => position !== 1)
    const discounts = postCheckoutPriceAdjustments.map((price_adjustment) => this.getDiscountFromPriceAdjustment(selling_plan, price_adjustment))
    return discounts
  }

  postCheckoutDiscountsText(sellingPlan = this.selectedSellingPlan) {
    if (!sellingPlan || !this.options?.future_price_adjustments_text || this.future_price_adjustments_text == '') return

    const postCheckoutPriceAdjustments = this.postCheckoutPriceAdjustments(sellingPlan)
    if (!postCheckoutPriceAdjustments.length) return

    return postCheckoutPriceAdjustments.map((discount, index) =>
      this.options?.future_price_adjustments_text
        .replaceAll('[discount]', this.options?.discount_format === 'absolute' ? this.money(discount.absolute) : discount.percent + '%')
        .replaceAll('[order_count]', index + 1)
        .replaceAll('[order_count_ordinal]', this.appendOrdinalSuffix(index + 1))
    )
  }

  getDiscountFromPriceAdjustment(selling_plan, price_adjustment) {
    const discount = {
      percent: 0,
      amount: 0,
      absolute: 0
    }

    let multiplier = 1

    let sellingPlanGroup = this.product.selling_plan_groups.find((group) => group.selling_plans.some((plan) => plan.id == selling_plan.id))

    if (sellingPlanGroup?.name === 'Prepaid') {
      const str = selling_plan.name // replace with your string
      const intervalDate = /\b\d+\s*(days|weeks|months|years)\b/gi
      const intervalDateMatches = str.match(intervalDate)

      const intervals = intervalDateMatches[0].match(/\d+/g)
      multiplier = intervals[0] / (intervals[1] || 1)
      multiplier = multiplier > 1 ? multiplier : 1
    }

    const price = this.selectedVariant.price
    const compareAtPrice =
      this.selectedVariant.compare_at_price && this.selectedVariant.compare_at_price > price ? this.selectedVariant.compare_at_price : price

    switch (price_adjustment.value_type) {
      case 'percentage':
        discount.percent = price_adjustment.value
        discount.absolute = Math.round((price * price_adjustment.value) / 100.0)
        discount.amount = Math.round((price * price_adjustment.value) / 100.0)
        break
      case 'fixed_amount':
        discount.percent = Math.round(((price_adjustment.value * 1.0) / price) * 100.0)
        discount.absolute = price_adjustment.value
        discount.amount = price_adjustment.value
        break
      case 'price':
        discount.percent = Math.round((((compareAtPrice * multiplier - price_adjustment.value) * 1.0) / (compareAtPrice * multiplier)) * 100.0)
        discount.absolute = compareAtPrice * multiplier - price_adjustment.value
        discount.amount = price - price_adjustment.value
        break
    }

    return discount
  }
  // !SECTION: Discount Functions
  price(selling_plan, formatted = true) {
    if (this.rule) {
      return formatted
        ? this.money(this.selectedVariant.price - this.discount(selling_plan).amount - this.selectedVariant.price * (this.rule.discountAmount / 100))
        : this.selectedVariant.price - this.discount(selling_plan).amount - this.selectedVariant.price * (this.rule.discountAmount / 100)
    }

    return formatted
      ? this.money(this.selectedVariant.price - this.discount(selling_plan).amount)
      : this.selectedVariant.price - this.discount(selling_plan).amount
  }

  // Updates element data to be registered by forms
  updateForm() {
    let $sellingPlan = this.querySelector('input[name=selling_plan]')

    if ($sellingPlan) {
      $sellingPlan.value = this.selectedSellingPlan ? this.selectedSellingPlan.id : ''
      $sellingPlan.dispatchEvent(new Event('change'))
    }
  }

  // SECTION: Additional Functionality
  updateURLParams() {
    // First check if we're in a browser environment and have all required objects
    if (typeof window === 'undefined' || 
        !window.location || 
        !window.history || 
        typeof window.history.replaceState !== 'function') {
      return;
    }

    try {
      const url = new URL(window.location.href);

      // Only update URL if we have a valid selectedSellingPlan with an id
      if (this.selectedSellingPlan?.id) {
        url.searchParams.set('selling_plan', this.selectedSellingPlan.id);
      } else {
        url.searchParams.delete('selling_plan');
      }

      // Use a more defensive approach to update history
      try {
        window.history.replaceState({}, '', url.href);
      } catch (historyError) {
        console.warn('Error updating browser history:', historyError);
        // Fallback to just updating the URL without history
        window.location.href = url.href;
      }
    } catch (error) {
      console.warn('Error updating URL parameters:', error);
    }
  }

  // Runs a fetch request to add the selectedVariant to the cart with the passed quantity and selectedSellingPlan
  addToCart(quantity) {
    const items = [
      {
        id: this.selectedVariant.id,
        quantity: quantity,
        ...(this.selectedSellingPlan && {
          selling_plan: this.selectedSellingPlan?.id
        })
      }
    ]

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        items
      })
    })
      .then((response) => response.json())
      .then((response) => {
        this.log('Added item to cart: ', response)
        //dispatch CustomEvent to tell document that an item was added to cart
        const event = new CustomEvent(`skio::added-to-cart`, {
          bubbles: true,
          composed: true,
          detail: {
            response,
            key: this.key
          }
        })

        this.dispatchEvent(event)
      })
      .catch((error) => {
        this.error(`SKIO ${this.key} error adding item to cart: `, error)
      })
  }

  fetchProduct = (handle) => {
    return fetch(`/products/${handle}.js`)
      .then((response) => response.json())
      .then((product) => {
        this.product = product
        this.selectedVariant = product.variants[0]

        return product
      })
  }
}

if (!customElements.get('skio-plan-picker')) {
  customElements.define('skio-plan-picker', SkioPlanPicker)
}

if (window) window.SkioPlanPicker = SkioPlanPicker
